import { FC, useContext, useEffect, useState } from 'react';
import { Alert, Button, Portal, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router';
import { UserContext } from '../..';
import { ROLE_ADMIN } from '../../constants';
import UserFormDialog from '../CreateUser';
import Home from '../Home';
import ChangePasswordDialog from './ChangePasswordDialog';

// "Mon profil", `/profile` (no design in the XD, gap 3).
//
// It is the Home with the read-only user modal (UserFormDialog in
// `view`, the same one `/view-user/:id` opens over the list) over the own
// account, titled "Mon profil". Closing it returns to "Mes documents".
// It replaces the previous profile page.
//
// Actions: "Changer le mot de passe" opens a second modal
// (ChangePasswordDialog). "Modifier" only for the administrator, who goes to
// `/edit-user/:id`: `PATCH /users/` is `requireAdmin`, a USER cannot
// edit their own profile (API gap, noted) and it is not offered to them.
//
// The success notice goes in a Snackbar inside a Portal: the profile
// modal is still open, and a Snackbar painted inside the shell would sit
// under its backdrop (same reason as in ArchiveWizard).

const Profile: FC = () => {
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const me = userContext?.mainUser ?? null;
  const isAdmin = me?.role === ROLE_ADMIN;

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [notice, setNotice] = useState<string>();

  useEffect(() => {
    document.title = 'ArchivOs - Mon profil';
  }, []);

  const close = () => navigate('/my-documents');

  return (
    <Home scope="me" hideTiles>
      <UserFormDialog
        open
        mode="view"
        userId={me?.id}
        title="Mon profil"
        canEdit={isAdmin}
        onClose={close}
        onMissing={() => navigate('/404')}
        extraActions={
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setPasswordOpen(true)}
          >
            Changer le mot de passe
          </Button>
        }
      />

      {me?.id && (
        <ChangePasswordDialog
          open={passwordOpen}
          userId={me.id}
          onClose={() => setPasswordOpen(false)}
          onChanged={() => {
            setPasswordOpen(false);
            setNotice('Mot de passe modifié.');
          }}
        />
      )}

      <Portal>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(notice)}
          autoHideDuration={6000}
          disableWindowBlurListener
          onClose={() => setNotice(undefined)}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setNotice(undefined)}
            sx={{ width: '100%' }}
          >
            {notice}
          </Alert>
        </Snackbar>
      </Portal>
    </Home>
  );
};

export default Profile;
